import {
  AddRegular,
  ArrowUpRegular,
  ComposeRegular,
  MapIcon,
  MoreHorizontalRegular,
  PersonIcon,
  ResourceIcon,
  Briefcase,
  CopyRegular,
  EditRegular
} from "@/pages/taskpane/components/icons/IconCustom";
import Button from "@/pages/taskpane/components/ui/Button";
import Input from "@/pages/taskpane/components/ui/Input";
import { useSettingStore, useThemeStore, useToastStore } from "@/pages/taskpane/shared/stores";
import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { useAgentChat } from "../services/useAgentChat";
import { Message } from "../services/types";

// Debounce utility function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Premium Thinking Indicator with pulsar effect and glassmorphism
const ThinkingIndicator = memo(({ content }: { content?: string | null }) => {
  return (
    <div className="flex flex-col gap-2 py-3 px-4 bg-white/40 dark:bg-white/5 backdrop-blur-md border border-gray-200/50 dark:border-white/10 rounded-2xl shadow-sm self-start mb-2 animate-in fade-in slide-in-from-left-4 duration-500 max-w-[90%]">
      <div className="flex items-center gap-3">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-4 h-4 bg-primaryBlue/20 dark:bg-brightBlue/20 rounded-full animate-ping"></div>
          <div className="relative w-2 h-2 bg-primaryBlue dark:bg-brightBlue rounded-full shadow-[0_0_8px_rgba(0,120,212,0.5)]"></div>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-primaryBlue dark:text-brightBlue opacity-80">
          Thinking ...
        </span>
      </div>

      {content ? (
        <div className="flex flex-col gap-1 pl-5 border-l border-primaryBlue/20 dark:border-brightBlue/20 ml-1">
          <span className="text-xs text-darkGray dark:text-lightGray italic leading-relaxed animate-pulse">
            {content}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 pl-5">
          <span className="w-1 h-1 rounded-full bg-mediumGray dark:midGray animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-1 h-1 rounded-full bg-mediumGray dark:midGray animate-bounce" style={{ animationDelay: '200ms' }}></span>
          <span className="w-1 h-1 rounded-full bg-mediumGray dark:midGray animate-bounce" style={{ animationDelay: '400ms' }}></span>
        </div>
      )}
    </div>
  );
});

ThinkingIndicator.displayName = 'ThinkingIndicator';

// MessageItem component - memoized for performance
interface MessageItemProps {
  msg: Message;
  isDarkMode: boolean;
  onCopy: (text: string) => void;
  onEdit: (text: string) => void;
}

const MessageItem = memo(({ msg, isDarkMode, onCopy, onEdit }: MessageItemProps) => {
  return (
    <div
      className={`group relative flex items-center max-w-[90%] w-fit ${msg.role === "user" ? "self-end" : "self-start"
        }`}
    >
      <div
        className={`py-3 px-5 rounded-[16px] w-[fit-content] ${msg.role === "user"
          ? "bg-lightBlue dark:bg-midnightNavy"
          : "bg-gray-100 dark:bg-gray-700"
          }`}
      >
        <span className="text-sm text-darkGray dark:text-lightGray whitespace-pre-wrap">{msg.content}</span>
      </div>

      {/* Hover actions */}
      <div
        className={`absolute top-0 flex items-center gap-1 p-1 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-gray-100 dark:border-gray-600 ${msg.role === "user" ? "right-full mr-2" : "left-full ml-2"
          }`}
      >
        <button
          onClick={() => onCopy(msg.content)}
          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
          title="Copy"
        >
          <CopyRegular isDarkMode={isDarkMode} size={14} />
        </button>
        {msg.role === "user" && (
          <button
            onClick={() => onEdit(msg.content)}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Edit"
          >
            <EditRegular isDarkMode={isDarkMode} size={14} />
          </button>
        )}
      </div>
    </div>
  );
}, (prev, next) => {
  // Custom comparison: only re-render if content changes
  return prev.msg.content === next.msg.content && 
         prev.msg.id === next.msg.id &&
         prev.isDarkMode === next.isDarkMode;
});

MessageItem.displayName = 'MessageItem';

const Agent = () => {
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const { t } = useTranslation();
  const { messages, isLoading, thinkingStep, sendMessage, resetChat } = useAgentChat();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Memoize suggestion options to prevent recreation on every render
  const suggestionOptions = useMemo(() => [
    {
      icon: <MapIcon isDarkMode={isDarkMode} isActive={true} />,
      title: "Find a meeting room by location",
      description: "Show available rooms in Building A",
    },
    {
      icon: <MapIcon isDarkMode={isDarkMode} isActive={true} />,
      title: "Find a resource by Resource Type",
      description: "I need a desk for work",
    },
    {
      icon: <PersonIcon isDarkMode={isDarkMode} isActive={true} />,
      title: "Find a meeting room by Capacity",
      description: "Find a room for 10 people",
    },
    {
      icon: <Briefcase isDarkMode={isDarkMode} />,
      title: "Find a meeting room by items",
      description: "Which room has tea and coffee service?",
    },
    {
      icon: <Briefcase isDarkMode={isDarkMode} />,
      title: "Request specific equipment",
      description: "I need a room with a projector and a whiteboard",
    },
  ], [isDarkMode]);

  // Improved scroll detection using IntersectionObserver
  useEffect(() => {
    if (!messagesEndRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAutoScroll(entry.isIntersecting);
      },
      { threshold: 0.9 }
    );
    
    observer.observe(messagesEndRef.current);
    return () => observer.disconnect();
  }, [messages.length]);

  // Auto scroll logic: scroll if autoScroll is enabled (user at bottom)
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, autoScroll]);

  // Debounced scroll handler to reduce re-renders
  const handleScroll = useCallback(
    debounce((e: React.UIEvent<HTMLDivElement>) => {
      // Safe check: e.currentTarget might be null when debounce executes
      if (!e.currentTarget) return;
      
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      // Check if user is near bottom (within 50px)
      const atBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(atBottom);
    }, 100),
    []
  );

  // Memoize handlers to prevent recreation
  const handleCopy = useCallback((text: string) => {
    if (!text) return;

    const fallbackCopyTextToClipboard = (text: string) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showToast("Copied to clipboard", true);
        } else {
          showToast("Failed to copy", false);
        }
      } catch (err) {
        showToast("Failed to copy", false);
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => showToast("Copied to clipboard", true))
        .catch(() => fallbackCopyTextToClipboard(text));
    } else {
      fallbackCopyTextToClipboard(text);
    }
  }, [showToast]);

  const handleEdit = useCallback((text: string) => {
    setMessage(text);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (message.trim() && !isLoading) {
      setAutoScroll(true); // Force scroll for new message
      sendMessage(message);
      setMessage("");
    }
  }, [message, isLoading, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        handleSendMessage();
      }
    }
  }, [isLoading, handleSendMessage]);

  const renderMessageSent = useCallback(() => {
    return (
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex flex-col px-5 py-3 flex-1 overflow-y-auto no-scrollbar"
      >
        <div className="flex gap-2 items-center w-full">
          <span className="text-xs font-semibold text-mediumGray dark:midGray">Today</span>
          <div className="border-b border-lightGray dark:border-darkMediumGray w-full flex-1"></div>
        </div>
        <div className="flex flex-col gap-3 mt-6">
          {messages.map((msg, index) => {
            const isLast = index === messages.length - 1;
            const showThinking = isLast && isLoading;

            return (
              <React.Fragment key={msg.id}>
                {/* 1. If thinking, show it first (above content) */}
                {showThinking && (thinkingStep || !msg.content.trim()) && (
                  <ThinkingIndicator content={thinkingStep} />
                )}

                {/* 2. Then show the message content */}
                {msg.content.trim().length > 0 && (
                  <MessageItem
                    msg={msg}
                    isDarkMode={isDarkMode}
                    onCopy={handleCopy}
                    onEdit={handleEdit}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>
    );
  }, [messages, isLoading, thinkingStep, isDarkMode, handleScroll, handleCopy, handleEdit]);

  const handleSuggestionClick = useCallback((description: string) => {
    setMessage(description);
  }, []);

  const renderSuggestions = useCallback(() => {
    return (
      <div className="flex flex-col gap-4 px-5 py-3 flex-1 overflow-y-auto no-scrollbar">
        <span className="text-lg font-semibold text-black dark:text-white">{t("Hi there. How can I help you today?")}</span>
        {suggestionOptions.map((option) => (
          <div
            key={option.title}
            className="flex flex-col gap-3 py-4 px-3 shadow-elevationShadow4 dark:shadow-elevationShadow4Dark rounded-lg bg-colorNeutralBackground1 dark:bg-colorNeutralBackground1Dark cursor-pointer"
            onClick={() => handleSuggestionClick(option.description)}
          >
            <div className="flex items-center gap-2">
              <div>{option.icon}</div>
              <span className="text-sm font-semibold text-black dark:text-white">{option.title}</span>
            </div>
            <span className="text-sm text-black dark:text-white">{option.description}</span>
          </div>
        ))}
      </div>
    );
  }, [suggestionOptions, t, handleSuggestionClick]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-3 justify-end py-3 px-5">
        <Button
          className="h-[32px] w-[32px] bg-primaryBlue hover:bg-deepBlue dark:hover:bg-freshSkyBlue dark:bg-brightBlue"
          leftIcon={<ComposeRegular isDarkMode={isDarkMode} />}
          onClick={resetChat}
        />
        {/* <Button className="h-[32px] w-[32px]" iconButton={<MoreHorizontalRegular isDarkMode={isDarkMode} />} /> */}
      </div>
      {messages.length ? renderMessageSent() : renderSuggestions()}
      <div className="pb-5 px-3">
        <div className="px-3 py-3 shadow-elevationShadow4 dark:shadow-elevationShadow4Dark rounded-lg bg-colorNeutralBackground1 dark:bg-colorNeutralBackground1Dark">
          <div className="flex flex-col gap-3">
            <textarea
              placeholder="Find your resource..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="flex-1 w-full outline-none bg-transparent resize-none text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              rows={2}
            />
            <div className="flex items-center justify-between">
              <Button className="h-[32px] w-[32px]" leftIcon={<AddRegular isDarkMode={isDarkMode} />} />
              <Button
                className="h-[32px] w-[32px] bg-darkGray dark:bg-lightGray rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                leftIcon={<ArrowUpRegular isDarkMode={!isDarkMode} />}
                onClick={handleSendMessage}
                disabled={isLoading || !message.trim()}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agent;
